import { useT } from "@transifex/react";
import moment, { type Moment } from "moment";
import { useEffect, useState } from "react";
import BasePopupWrapper, { TitleColorVariants } from "../../basic/popups/BasePopupWrapper";
import DateTimeInput from "../../basic/dateTimeInput/DateTimeInput";
import FormInfoRow from "../../basic/FormInfoRow";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import { useNow } from "../../hooks/useNow";
import { ACTIVITY_VISIBILITY_ICONS, ActivityVisibility } from "../../helpers/ActivityVisibility";
import { isMomentInPast } from "../../utils/dateUtils";
import { ClassActivityCategory, selectedCardStyles } from "../../constants/ClassActivityConstants";
import { ButtonColorVariant } from "../teacherArea/AreaButton";

export type EditActivityVisibilityTheme = "test" | "vocab";

interface EditActivityVisibilityModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialVisibility: ActivityVisibility;
    initialPlannedDate: Moment | null;
    deadline?: Moment | null;
    theme: EditActivityVisibilityTheme;
    onSave: (next: { visibility: ActivityVisibility; plannedDate: Moment | null }) => Promise<void> | void;
}

const OPTION_ORDER: ActivityVisibility[] = [
    ActivityVisibility.HIDDEN,
    ActivityVisibility.PLANNED,
    ActivityVisibility.VISIBLE,
];

const EditActivityVisibilityModal = (props: EditActivityVisibilityModalProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();
    // Re-render each minute so the past-date info notice below tracks wall-clock time while the modal stays open.
    useNow();

    const deadlineDate = props.deadline && props.deadline.isValid() ? props.deadline : undefined;

    const tags = { _tags: "ClassActivity, EditActivityVisibilityModal" };
    const t_title = t("Edit visibility", tags);
    const t_hidden = t("Hidden", tags);
    const t_planned = t("Planned", tags);
    const t_visible = t("Visible", tags);
    const t_visibleFrom = t("Visible from:", tags);
    const t_cancel = t("Cancel", tags);
    const t_save = t("Save", tags);
    const t_visibleImmediately = t("Will be visible immediately because the selected time is in the past.", tags);
    const t_plannedInfoText = deadlineDate
        ? t("The deadline is on {date}, {time}. The visibility date cannot be after that.", {
              ...tags,
              date: deadlineDate.format("DD.MM.YYYY"),
              time: deadlineDate.format("HH:mm"),
          })
        : undefined;

    const labels: Record<ActivityVisibility, string> = {
        [ActivityVisibility.HIDDEN]: t_hidden,
        [ActivityVisibility.PLANNED]: t_planned,
        [ActivityVisibility.VISIBLE]: t_visible,
    };

    const [selected, setSelected] = useState<ActivityVisibility>(props.initialVisibility);
    const [plannedDate, setPlannedDate] = useState<Moment | null>(props.initialPlannedDate);
    const [isSaving, setIsSaving] = useState(false);

    // Keyed on the primitive timestamp: the Moment prop is a fresh instance on every parent render,
    // and an identity-keyed reset would revert in-progress edits whenever the parent re-renders.
    const initialPlannedDateMs = props.initialPlannedDate?.valueOf() ?? null;
    useEffect(() => {
        if (props.isOpen) {
            setSelected(props.initialVisibility);
            setPlannedDate(initialPlannedDateMs !== null ? moment(initialPlannedDateMs) : null);
            setIsSaving(false);
        }
    }, [props.isOpen, props.initialVisibility, initialPlannedDateMs]);

    const isVocab = props.theme === "vocab";
    const activityCategory = isVocab ? ClassActivityCategory.VOCAB_TRAINING : ClassActivityCategory.TEST;

    const selectedClasses = selectedCardStyles[activityCategory];
    const idleClasses = "border border-p6-grey-mid bg-white";

    const titleColor = isVocab ? TitleColorVariants.SECONDARY : TitleColorVariants.DEFAULT;
    const primaryColor = isVocab ? ButtonColorVariant.SECONDARY : ButtonColorVariant.DEFAULT;

    const isPlanned = selected === ActivityVisibility.PLANNED;
    const isPlannedDateMissing = isPlanned && !plannedDate?.isValid();
    const hasChanges =
        selected !== props.initialVisibility ||
        (isPlanned && (plannedDate?.valueOf() ?? null) !== initialPlannedDateMs);
    // A past planned time doesn't block saving — the activity just becomes visible right away, and
    // the info notice below the picker says so.
    const canSave = !isPlannedDateMissing && !isSaving && hasChanges;

    const handleSelect = (option: ActivityVisibility) => {
        setSelected(option);
    };

    const handleSave = async () => {
        if (!canSave) return;
        setIsSaving(true);
        try {
            await props.onSave({
                visibility: selected,
                plannedDate: isPlanned ? plannedDate : null,
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <BasePopupWrapper
            isOpen={props.isOpen}
            onClose={props.onClose}
            title={t_title}
            titleColor={titleColor}
            maxWidth={962}
            primaryButton={{
                text: t_save,
                onClick: handleSave,
                disabled: !canSave,
                colorVariant: primaryColor,
            }}
            secondaryButton={{
                text: t_cancel,
                onClick: props.onClose,
                colorVariant: primaryColor,
            }}
        >
            <div className="flex flex-col gap-m">
                <div className={`grid gap-m ${getStyleBasedOnBreakpoint(Breakpoint.MD, "grid-cols-3", "grid-cols-1")}`}>
                    {OPTION_ORDER.map((option) => {
                        const isSelected = option === selected;
                        return (
                            <button
                                key={option}
                                type="button"
                                data-testid={`activity-visibility-option-${option}`}
                                onClick={() => handleSelect(option)}
                                className={`flex items-center justify-center gap-s rounded-medium p-m text-xl font-semibold text-p6-grey-dark cursor-pointer min-w-0 ${
                                    isSelected ? selectedClasses : idleClasses
                                }`}
                                aria-pressed={isSelected}
                            >
                                <PhaseSixIcon
                                    name={ACTIVITY_VISIBILITY_ICONS[option]}
                                    className="text-xxl shrink-0"
                                />
                                <span className="truncate">{labels[option]}</span>
                            </button>
                        );
                    })}
                </div>

                {isPlanned && (
                    <div className="flex flex-col gap-xs">
                        <p className="text-l text-p6-grey-dark">{t_visibleFrom}</p>
                        <DateTimeInput
                            value={plannedDate}
                            onChange={setPlannedDate}
                            minDate={moment()}
                            maxDate={deadlineDate}
                            maxTime={deadlineDate}
                            infoText={t_plannedInfoText}
                        />
                        {isMomentInPast(plannedDate) && (
                            <FormInfoRow warningCategory={activityCategory}>{t_visibleImmediately}</FormInfoRow>
                        )}
                    </div>
                )}
            </div>
        </BasePopupWrapper>
    );
};

export default EditActivityVisibilityModal;
